import { EnvelopeIcon, PencilSquareIcon, PrinterIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailValue, hasToEmails, LoanPartiesEmailTo } from 'components/EmailTo'
import { LayoutLoading } from 'components/LayoutLoading'
import { SaveChanges } from 'components/SaveChanges'
import { type InputType, noteColors } from 'config'
import type { EmailTemplate } from 'pages/Admin'
import Disbursement from 'pages/Loan/Funding/Disbursement'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Prompt } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  downloadFile,
  downloadNetFundingSheet,
  getEmailTemplatesGroup,
  gethud1PageData,
  posthud1Notes,
  posthud1Page1Data,
  sendNetFundingSheetEmail,
} from 'services'
import Api from 'services/api'
import { svgLoading } from 'stories/assets'
import { Button, Modal, PlainTable } from 'stories/components'
import { ClosingCostsDetailsModal } from 'stories/components/ClosingCostsDetailsModal/ClosingCostsDetailsModal'
import { CustomContextMenu } from 'stories/components/CustomContextMenu'
import { FieldNote } from 'stories/components/FieldNote'
import { InterimInterestModal } from 'stories/components/InterimInterestModal'
import { MIPFundingFeeModal } from 'stories/components/MIPFundingFeeModal'
import { OverrideCaclModal } from 'stories/components/OverrideCalcModal/OverrideCalcModal'
import { PrepaidItemsDetailsModal } from 'stories/components/PrepaidItemDetailsModal/PrepaidItemDetailsModal'
import {
  buildOverviewIdentityPlaceholders,
  formatDateYMD,
  getPrice1or2decimal,
  getPrice2decimal,
  isEmpty,
  Num,
  openAuditLog,
  removeComma,
} from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { calculateAggregateAdjustment, renderAggregateAdjustmentValue } from '../HUD1Page2'

const tableHeader = ['', 'Total', 'Lender Paid', 'P.O.C.', 'Net From Wire']

export default function Funding() {
  const [action, setAction] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [changed, setChanged] = useState(false)
  const [inputs, setInputs] = useState<any>({})
  const [data, setData] = useState<any>({})
  const [orgData, setOrgData] = useState<any>({})
  const [notes, setNotes] = useState<any>({})
  const [IDs, setIDs] = useState<Array<string>>([])

  const [showFeeWithValue, setShowFeeWithValue] = useState(true)
  const [showEscrowedItem, setShowEscrowedItem] = useState(true)

  const [ccData, setCCData] = useState<Array<Array<any>>>([])
  const [ppData, setPPData] = useState<Array<Array<any>>>([])

  const [ClosingCosts, setClosingCosts] = useState<Array<Record<string, string>>>([])
  const [PrepaidItems, setPrepaidItems] = useState<Array<Record<string, string>>>([])
  const [Parties, setParties] = useState<Array<Record<string, string>>>([])
  const [loanParties, setLoanParties] = useState<Array<Record<string, any>>>([])
  const [closingCostsDetailData, setClosingCostsDetailData] = useState<any>({})
  const [prepaidItemsDetailData, setPrepaidItemsDetailData] = useState<any>({})
  const [showMIPFundingModal, setShowMIPFundingModal] = useState(false)
  const [showInterimInterestModal, setShowInterimInterestModal] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [netFundingEmailRecipients, setNetFundingEmailRecipients] = useState<EmailValue>({})
  const [emailModalLoading, setEmailModalLoading] = useState(false)
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([])
  const [selectTypeInput, setSelectTypeInput] = useState<InputType>({
    inputType: 'select',
    options: {},
    title: 'Type',
    required: false,
    hasDefaultOption: true,
  })
  const [contentInput, setContentInput] = useState<InputType>({
    inputType: 'textarea',
    title: 'Content',
    rows: 10,
  })
  const [totalCCNetfromWires, setTotalCCNetfromWire] = useState(0)

  const loanDetail = useSelector((state: any) => state.loanDetail)
  const placeholderMap = useMemo(
    () =>
      buildOverviewIdentityPlaceholders({
        loanNumber: loanDetail.loanNumber,
        byteproFileName: loanDetail.byteproFileName,
        borrowingEntityName: loanDetail.borrowingEntityName,
        entityTitle: loanDetail.entityTitle,
        borrowerName: loanDetail.borrowerName,
        propertyAddress: loanDetail.propertyAddress,
        productType: loanDetail.productType,
        loanStatus: loanDetail.loanStatus,
        closingDate: loanDetail.closingDate,
        mers: loanDetail.mers,
      }),
    [loanDetail],
  )

  const [editField, setEditField] = useState({ key: '', value: null })
  const [ovData, setOVData] = useState<any>({})
  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    key: '',
    value: null,
  })

  const containerRef = useRef<HTMLDivElement | null>(null)

  const loanNumber = Api.getLoanNumber()
  const initData = async () => {
    setAction('')
    setIsLoading(true)
    const res = await gethud1PageData('funding')
    if (res.success) {
      setInputs(res.inputs)
      setData(res.data)
      setOrgData(res.data)
      setClosingCosts(res.ClosingCosts)
      setPrepaidItems(res.PrepaidItems)
      setParties(res.Parties)
      setLoanParties(res.loanParties || [])
      setIDs(res.IDs)
      setNotes(res.notes)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    initData()
  }, [])

  useEffect(() => {
    if (contextMenu.visible) {
      document.addEventListener('click', closeContextMenu)
    } else {
      document.removeEventListener('click', closeContextMenu)
    }

    // Cleanup when component unmounts or when context menu is closed
    return () => {
      document.removeEventListener('click', closeContextMenu)
    }
  }, [contextMenu.visible])

  useEffect(() => {
    const tempData: Array<any> = []
    const totalRow: Array<any> = [
      <p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">Total</p>,
      0,
      0,
      0,
      0,
    ]
    inputs['closingCosts']?.map((item: any) => {
      if (!showFeeWithValue || data[item.feeBKey] + data[item.feeSKey] != 0) {
        const row: any = [
          <span
            className="cursor-pointer hover:text-shade-blue hover:underline"
            onClick={() => {
              setClosingCostsDetailData({
                hudNo: { value: data[item.lineNoKey], key: item.lineNoKey },
                lineName: { value: item.title, key: item.titleKey },
                isPoints: {
                  value:
                    (data[item.lineNoKey] == 801 && [32, 34].includes(data[item.ClosingCostTypeKey])) ||
                    data[item.lineNoKey] == 802,
                  key: 'isPoints',
                },
                points: { value: data[item.feeMultipleKey], key: item.feeMultipleKey },
                borrowerAmount: { value: data[item.feeBKey], key: item.feeBKey },
                sellerAmount: { value: data[item.feeSKey], key: item.feeSKey },
                TRIDBlock: { value: data[item.TRIDBlockKey], key: item.TRIDBlockKey },
                GFEBlock: { value: data[item.GFEBlockKey], key: item.GFEBlockKey },
                ClosingCostType: { value: data[item.ClosingCostTypeKey], key: item.ClosingCostTypeKey },
                PaidToType: { value: data[item.PaidToTypeKey], key: item.PaidToTypeKey },
                Financed: { value: data[item.FinancedKey], key: item.FinancedKey },
                ResponsiblePartyType: { value: data[item.ResponsiblePartyTypeKey], key: item.ResponsiblePartyTypeKey },
                NetFromWire: { value: data[item.NetFromWireKey], key: item.NetFromWireKey },
                NotCounted: { value: data[item.NotCountedKey], key: item.NotCountedKey },
                ProviderChosenByBorrower: {
                  value: data[item.ProviderChosenByBorrowerKey],
                  key: item.ProviderChosenByBorrowerKey,
                },
                PaidByOtherType: { value: data[item.PaidByOtherTypeKey], key: item.PaidByOtherTypeKey },
                poc: { value: data[item.pocKey], key: item.pocKey },
                PPFC: { value: data[item.PPFCKey], key: item.PPFCKey },
                lineId: { value: data[item.lineIdKey], key: item.lineIdKey },
                PointsAndFeesAmountOV: {
                  value: data[item.PointsAndFeesAmountOVKey],
                  key: item.PointsAndFeesAmountOVKey,
                },
                PaidTo: { value: data[item.PaidToNameKey], key: item.PaidToNameKey },
                isPOCDisabled: { value: item?.isPOCDisabled, key: '' },
                partyType: { value: item?.partyType, key: '' },
                partyName: { value: data[item.defaultValueKey], key: '' },
                ccid: { value: item.ccid, key: '' },
              })
            }}
          >{`${item.title ? item.title : data[item.titleKey]} (${item.key})`}</span>,
          getPrice2decimal(+data[item.feeBKey] + +data[item.feeSKey], false, true),
          getPrice2decimal(data[item.PaidByOtherTypeKey] == 1 ? data[item.feeSKey] : '', false, true),
          getPrice2decimal(data[item.pocKey] == true ? +data[item.feeBKey] + +data[item.feeSKey] : '', false, true),
          getPrice2decimal(
            data[item.NetFromWireKey] == true && !data[item.pocKey] ? +data[item.feeBKey] + +data[item.feeSKey] : '',
            false,
            true,
          ),
        ]
        for (let i = 1; i < 5; i++) totalRow[i] = Number(totalRow[i]) + Number(removeComma(row[i]))
        tempData.push(row)
      }
    })
    if (!showFeeWithValue) tempData.push(['Mortgage Insurance (902)', '', '', '', ''])
    inputs['prepaidItems']?.map((item: any, index: number) => {
      if (![0, 5].includes(index)) return
      const prefix = `PrepaidItems.${index}.`
      if (
        showFeeWithValue &&
        !Number(data[`${prefix}PremiumOV`] || data[`${prefix}MonthsInAdvance`] * data[`${prefix}Payment`])
      )
        return
      const row = [
        <span
          className="cursor-pointer hover:text-shade-blue hover:underline"
          onClick={() =>
            setPrepaidItemsDetailData({
              title: [3, 7].includes(data[`${prefix}PrepaidItemType`]) ? data[`${prefix}NameOV`] : item,
              payment: { value: data[`${prefix}Payment`], key: `${prefix}Payment` },
              periodicPayment: {
                value: data[`${prefix}PeriodicPaymentAmount`],
                key: `${prefix}PeriodicPaymentAmount`,
              },
              disbSched: { value: data[`${prefix}DisbursementSched`], key: `${prefix}DisbursementSched` },
              starting: { value: data[`${prefix}DisbursementStartPur`], key: `${prefix}DisbursementStartPur` },
              cushion: { value: data[`${prefix}_Cushion`], key: `${prefix}_Cushion` },
              cushionOV: { value: data[`${prefix}CushionOV`], key: `${prefix}CushionOV` },
              monthsInAdvance: { value: data[`${prefix}MonthsInAdvance`], key: `${prefix}MonthsInAdvance` },
              premiumOV: { value: data[`${prefix}PremiumOV`], key: `${prefix}PremiumOV` },
              premiumPBS: { value: data[`${prefix}PremiumPBSDesired`], key: `${prefix}PremiumPBSDesired` },
              premiumPOC: { value: data[`${prefix}PremiumPOC`], key: `${prefix}PremiumPOC` },
              netFromWire: { value: data[`${prefix}PremiumNetFromWire`], key: `${prefix}PremiumNetFromWire` },
              paidTo: { value: data[`${prefix}PremiumPaidToType`], key: `${prefix}PremiumPaidToType` },
              premPointsAndFeesOV: {
                value: data[`${prefix}PremiumPointsAndFeesAmountOV`],
                key: `${prefix}PremiumPointsAndFeesAmountOV`,
              },
              monthsInReserve: { value: data[`${prefix}MonthsInReserve`], key: `${prefix}MonthsInReserve` },
              reserveOV: { value: data[`${prefix}ReservesOV`], key: `${prefix}ReservesOV` },
              reservePBS: { value: data[`${prefix}ReservesPBSDesired`], key: `${prefix}ReservesPBSDesired` },
              reservesPOC: { value: data[`${prefix}ReservesPOC`], key: `${prefix}ReservesPOC` },
              resPointsAndFeesOV: {
                value: data[`${prefix}ReservesPointsAndFeesAmountOV`],
                key: `${prefix}ReservesPointsAndFeesAmountOV`,
              },
              atrNotes: { value: data[`${prefix}QMATRNotes`], key: `${prefix}QMATRNotes` },
              premiumPaidByOther: {
                value: data[`${prefix}PremiumPaidByOtherType`],
                key: `${prefix}PremiumPaidByOtherType`,
              },
              reservePaidByOther: {
                value: data[`${prefix}ReservesPaidByOtherType`],
                key: `${prefix}ReservesPaidByOtherType`,
              },
              prepaidItemType: {
                value: data[`${prefix}MISMOPrepaidItemTypeOV`],
                key: `${prefix}MISMOPrepaidItemTypeOV`,
              },
              disbStartYear: {
                value: data[`${prefix}DisbursementStartYear`],
                key: `${prefix}DisbursementStartYear`,
              },
              disbPeriods: { value: data[`${prefix}DisbursementPeriods`], key: `${prefix}DisbursementPeriods` },
              disbList: { value: data[`${prefix}DisbursementList`], key: `${prefix}DisbursementList` },
            })
          }
        >
          {item || data[`${prefix}NameOV`]}
          {` (${index == 0 ? '903' : '904'})`}
        </span>,
        data[`${prefix}PremiumOV`] || data[`${prefix}MonthsInAdvance`] * data[`${prefix}Payment`] || '',
        data[`${prefix}PremiumPaidByOtherType`] == 1 ? data[`${prefix}PremiumPBSDesired`] : '',
      ]
      row.push(data[`${prefix}PremiumPOC`] == false ? '' : row[1] || '')
      row.push(
        data[`${prefix}PremiumPOC`] == true
          ? ''
          : data[`${prefix}PremiumNetFromWire`] == true
          ? data[`${prefix}PremiumPaidByOtherType`] == 1
            ? Number(row[1]) - 2 * +data[`${prefix}PremiumPBSDesired`]
            : Number(row[1])
          : data[`${prefix}PremiumPaidByOtherType`] == 1
          ? -Number(data[`${prefix}PremiumPBSDesired`])
          : '',
      )
      totalRow[1] = Number(totalRow[1]) + Number(row[1])
      totalRow[2] = Number(totalRow[2]) + Number(row[2])
      totalRow[4] = Number(totalRow[4]) + Number(row[4])
      for (let i = 1; i < 5; i++) row[i] = getPrice2decimal(row[i], false, true)
      tempData.push(row)
    })
    setTotalCCNetfromWire(totalRow[4])
    for (let i = 1; i < 5; i++)
      totalRow[i] = (
        <p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">
          ${getPrice1or2decimal(totalRow[i], false, true)}
        </p>
      )
    tempData.push(totalRow)
    setCCData(tempData)
  }, [showFeeWithValue, data])

  useEffect(() => {
    const tempData: Array<any> = []
    const totalRow: Array<any> = [
      <p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">Total</p>,
      0,
      0,
      0,
      0,
    ]
    inputs['prepaidItems']?.map((item: any, index: number) => {
      const prefix = `PrepaidItems.${index}.`
      if (
        showEscrowedItem &&
        !Number(data[`${prefix}ReservesOV`] || data[`${prefix}MonthsInReserve`] * data[`${prefix}Payment`])
      )
        return
      const row = [
        <span
          className="cursor-pointer hover:text-shade-blue hover:underline"
          onClick={() =>
            setPrepaidItemsDetailData({
              title: [3, 7].includes(data[`${prefix}PrepaidItemType`]) ? data[`${prefix}NameOV`] : item,
              payment: { value: data[`${prefix}Payment`], key: `${prefix}Payment` },
              periodicPayment: {
                value: data[`${prefix}PeriodicPaymentAmount`],
                key: `${prefix}PeriodicPaymentAmount`,
              },
              disbSched: { value: data[`${prefix}DisbursementSched`], key: `${prefix}DisbursementSched` },
              starting: { value: data[`${prefix}DisbursementStartPur`], key: `${prefix}DisbursementStartPur` },
              cushion: { value: data[`${prefix}_Cushion`], key: `${prefix}_Cushion` },
              cushionOV: { value: data[`${prefix}CushionOV`], key: `${prefix}CushionOV` },
              monthsInAdvance: { value: data[`${prefix}MonthsInAdvance`], key: `${prefix}MonthsInAdvance` },
              premiumOV: { value: data[`${prefix}PremiumOV`], key: `${prefix}PremiumOV` },
              premiumPBS: { value: data[`${prefix}PremiumPBSDesired`], key: `${prefix}PremiumPBSDesired` },
              premiumPOC: { value: data[`${prefix}PremiumPOC`], key: `${prefix}PremiumPOC` },
              netFromWire: { value: data[`${prefix}PremiumNetFromWire`], key: `${prefix}PremiumNetFromWire` },
              paidTo: { value: data[`${prefix}PremiumPaidToType`], key: `${prefix}PremiumPaidToType` },
              premPointsAndFeesOV: {
                value: data[`${prefix}PremiumPointsAndFeesAmountOV`],
                key: `${prefix}PremiumPointsAndFeesAmountOV`,
              },
              monthsInReserve: { value: data[`${prefix}MonthsInReserve`], key: `${prefix}MonthsInReserve` },
              reserveOV: { value: data[`${prefix}ReservesOV`], key: `${prefix}ReservesOV` },
              reservePBS: { value: data[`${prefix}ReservesPBSDesired`], key: `${prefix}ReservesPBSDesired` },
              reservesPOC: { value: data[`${prefix}ReservesPOC`], key: `${prefix}ReservesPOC` },
              resPointsAndFeesOV: {
                value: data[`${prefix}ReservesPointsAndFeesAmountOV`],
                key: `${prefix}ReservesPointsAndFeesAmountOV`,
              },
              atrNotes: { value: data[`${prefix}QMATRNotes`], key: `${prefix}QMATRNotes` },
              premiumPaidByOther: {
                value: data[`${prefix}PremiumPaidByOtherType`],
                key: `${prefix}PremiumPaidByOtherType`,
              },
              reservePaidByOther: {
                value: data[`${prefix}ReservesPaidByOtherType`],
                key: `${prefix}ReservesPaidByOtherType`,
              },
              prepaidItemType: {
                value: data[`${prefix}MISMOPrepaidItemTypeOV`],
                key: `${prefix}MISMOPrepaidItemTypeOV`,
              },
              disbStartYear: {
                value: data[`${prefix}DisbursementStartYear`],
                key: `${prefix}DisbursementStartYear`,
              },
              disbPeriods: { value: data[`${prefix}DisbursementPeriods`], key: `${prefix}DisbursementPeriods` },
              disbList: { value: data[`${prefix}DisbursementList`], key: `${prefix}DisbursementList` },
            })
          }
        >
          {item || data[`${prefix}NameOV`]}
        </span>,
        data[`${prefix}ReservesOV`] || data[`${prefix}MonthsInReserve`] * data[`${prefix}Payment`] || '',
        data[`${prefix}ReservesPaidByOtherType`] == 1 ? data[`${prefix}ReservesPBSDesired`] : '',
        '',
      ]
      row.push(
        (data[`${prefix}ReservesPOC`] == true || !data['Secondary.EscrowDepositIsNettedFromWire']
          ? ''
          : (row[1] || 0) - (row[2] || 0)) || '',
      )
      totalRow[1] = Num(totalRow[1]) + Num(row[1])
      totalRow[2] = Num(totalRow[2]) + Num(row[2])
      totalRow[4] = Num(totalRow[4]) + Num(row[4])
      for (let i = 1; i < 5; i++) row[i] = row[i] ? getPrice2decimal(Num(row[i].toFixed(2)), false, true) : ''
      tempData.push(row)
    })
    const aggregateAdjustment =
      Math.round((Num(data['Loan.AggregateAdjustmentOV']) || calculateAggregateAdjustment(data) || 0) * 100) / 100
    tempData.push([
      'Aggregate Adjustment',
      renderAggregateAdjustmentValue(data, { useParentheses: false }),
      '',
      '',
      data['Secondary.EscrowDepositIsNettedFromWire']
        ? renderAggregateAdjustmentValue(data, { useParentheses: false })
        : '',
    ])
    totalRow[1] += aggregateAdjustment
    totalRow[4] += data['Secondary.EscrowDepositIsNettedFromWire'] ? aggregateAdjustment : 0
    for (let i = 1; i < 5; i++)
      totalRow[i] = (
        <p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">
          ${getPrice1or2decimal(totalRow[i].toFixed(2), false, true)}
        </p>
      )
    tempData.push(totalRow)
    setPPData(tempData)
  }, [showEscrowedItem, data])

  const convData = () => {
    const tableData: any = [
      [
        <span
          className="cursor-pointer hover:text-shade-blue hover:underline"
          onClick={() => setShowMIPFundingModal(true)}
        >
          Conv. UFMIP
        </span>,
        '0.00',
        '',
        '',
        '0.00',
      ],
      [
        <span
          className="cursor-pointer hover:text-shade-blue hover:underline"
          onClick={() => setShowMIPFundingModal(true)}
        >
          Conv. UFMIP Paid in Cash
        </span>,
        getPrice2decimal(data['Loan.MIPFFPaidInCash'] || 0, false, true),
        data['Loan.MIPFFPaidInCashPaidByOtherType'] == 1
          ? getPrice2decimal(data['Loan.MIPFFPaidInCashPBSDesired'] || 0, false, true)
          : '',
        '',
        data['Secondary.MIPFFIsNettedFromWire']
          ? getPrice2decimal(
              Number(data['Loan.MIPFFPaidInCash']) -
                (data['Loan.MIPFFPaidInCashPaidByOtherType'] == 1
                  ? Number(data['Loan.MIPFFPaidInCashPBSDesired'] || 0)
                  : 0),
              false,
              true,
            )
          : '0.00',
        ,
      ],
    ]
    const totalRow = [<p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">Total</p>]
    for (let i = 1; i < 5; i++)
      totalRow[i] = (
        <p className="border-t-2 border-slate-500 border-separate pt-2 font-bold">
          ${getPrice2decimal(tableData[1][i], false, true)}
        </p>
      )
    tableData.push(totalRow)
    return tableData
  }

  const interimInterestData = () => {
    return [
      [
        <span
          className="cursor-pointer hover:text-shade-blue hover:underline"
          onClick={() => setShowInterimInterestModal(true)}
        >
          Interim Interest
        </span>,
        getPrice2decimal(data['Loan.InterimInterestOV'] || calculateValue('Loan._InterimInterest'), false, true),
        getPrice2decimal(
          data['Loan.InterimInterestPaidByOtherType'] == 1 ? data['Loan.InterimInterestPBSDesired'] || 0 : '0.00',
          false,
          true,
        ),
        '',
        data['Secondary.InterimInterestIsNettedFromWire']
          ? getPrice2decimal(
              Number(data['Loan.InterimInterestOV'] || calculateValue('Loan._InterimInterest')) -
                Number(
                  data['Loan.InterimInterestPaidByOtherType'] == 1 ? data['Loan.InterimInterestPBSDesired'] || 0 : 0,
                ),
              false,
              true,
            )
          : '0.00',
      ],
    ]
  }

  const onDeleteFee = () => {
    initData()
    toast('Successfully deleted fee', { type: 'success' })
  }

  const closeOVModal = (save: false, modalData: any) => {
    if (save) {
      const value = modalData.ovValue
      if (value === '' || value === null || value === undefined) onChange(ovData.overrideKey, '')
      else onChange(ovData.overrideKey, value, 'number')
    }
    setOVData({})
  }

  const applyTitleCompanyContactDisplay = (temp: Record<string, any>, partyType: string) => {
    const party = loanParties.find((p) => p.Type === partyType) || loanParties.find((p) => p.Type === 'Title Company') || {}
    const fullName = [party.FirstName, party.MiddleName, party.LastName].filter(Boolean).join(' ')
    const address =
      party.Street && party.City && party.State && party.Zip
        ? `${party.Street} ${party.City}, ${party.State} ${party.Zip}`
        : ''
    temp['TitleCompany.DisplayCompany'] = party.Company || ''
    temp['TitleCompany.DisplayFullName'] = fullName
    temp['TitleCompany.DisplayAddress'] = address
    temp['TitleCompany.DisplayEmail'] = party.EMail || ''
    temp['TitleCompany.DisplayPhone'] = party.WorkPhone || party.MobilePhone || party.HomePhone || ''
    return temp
  }

  const onChange = (key: string, value: any, type?: string, orgData?: Record<string, any>) => {
    const hasOrgData = !!orgData
    if (!orgData) orgData = data
    let temp = cloneDeep(orgData!)

    const params = key.split('.')
    if (params.length > 2 && params[2] == 'CushionOV') value = value.split('.')[0]

    if (type === 'number') temp[key] = getPrice1or2decimal(value).replace(',', '')
    else temp[key] = value

    if (key === 'TitleCompany.ContactPartyType') {
      applyTitleCompanyContactDisplay(temp, value)
    }

    if (!hasOrgData) {
      setData(temp)
      if (!changed) setChanged(true)
    } else return temp
  }

  const onSaveChanges = async () => {
    setChanged(false)
    let json: any = {}
    Object.keys(data).map((key) => {
      if (JSON.stringify(data[key]) != JSON.stringify(orgData[key])) json[key] = data[key]
    })
    if (Object.keys(json).length > 0) {
      json.IDs = IDs
      json.ClosingCosts = ClosingCosts
      json.PrepaidItems = PrepaidItems
      json.Parties = Parties
      setAction('saveChanges')
      await posthud1Page1Data('page1', json)
      setAction('')
      setOrgData(data)
    }
  }

  const closeCostingDetailModal = (save: false, detailData: any) => {
    if (save) {
      let newData = cloneDeep(data)
      Object.keys(detailData).map((v) => {
        const { key, value } = detailData[v]
        if (data[key] !== value && Object.keys(data).includes(key)) {
          newData = onChange(key, value, '', newData)
        }
      })
      setData(newData)
      setChanged(true)
    }
    setPrepaidItemsDetailData({})
    setClosingCostsDetailData({})
  }

  const closeMIPFundingModal = (save: boolean, detailData: any) => {
    if (save) {
      let newData = cloneDeep(data)
      Object.keys(detailData).map((key) => {
        if (data[key] !== detailData[key]) {
          newData = onChange(key, detailData[key], '', newData)
        }
      })
      setData(newData)
      setChanged(true)
    }
    setShowMIPFundingModal(false)
    setShowInterimInterestModal(false)
  }

  const renderSectionHeader = (title: string) => {
    return <p className="bg-blue-100 p-2 md:col-span-4 col-span-1">{title}</p>
  }

  const showHistory = async (keys: Array<string>) => {
    const fieldKeys = (keys || []).filter((key) => !!key)
    if (!fieldKeys.length) return
    const options = {
      table: 'HUD1',
      field: fieldKeys.join(', '),
      keys: {
        field: fieldKeys,
      },
      renderValue: (data: any) => <span dangerouslySetInnerHTML={{ __html: data }} />,
    }
    openAuditLog(options)
  }

  const calculateValue: any = (key: string) => {
    let CALCULATED_VALUE
    switch (key) {
      case 'ClosingCosts.808.TotalAmount':
        const { feeBKey, feeSKey } = inputs['closingCosts']?.find((v: any) => v.lineId == 808) || {}
        CALCULATED_VALUE = data[feeBKey] + data[feeSKey]
        break
      case 'Loan.UFMIP':
        CALCULATED_VALUE =
          0 -
          (data['Secondary.MIPFFIsNettedFromWire']
            ? Number(data['Loan.MIPFFPaidInCash'] || 0) -
              (data['Loan.MIPFFPaidInCashPaidByOtherType'] == 1
                ? Number(data['Loan.MIPFFPaidInCashPBSDesired'] || 0)
                : 0)
            : 0)
        break
      case 'Loan.Interim':
        CALCULATED_VALUE =
          0 -
          (data['Secondary.InterimInterestIsNettedFromWire']
            ? Number(data['Loan.InterimInterestOV'] || calculateValue('Loan._InterimInterest') || 0) -
              Number(data['Loan.InterimInterestPaidByOtherType'] == 1 ? data['Loan.InterimInterestPBSDesired'] || 0 : 0)
            : 0)
        break
      case 'Loan._DailyInterest':
        const roundTo = !data['Loan.InterimIntDecimalsOV'] ? 2 : Number(data['Loan.InterimIntDecimalsOV']) + 1
        const loanAmount = data['isDutch'] ? Num(data['HUD1.Line202Calc']) : Num(data['Loan.initialLoanAmount'])
        CALCULATED_VALUE =
          Math.round(
            ((loanAmount *
              (data['Loan.BDInterimIntCalcMethodOV'] == 3 && data['Loan.LoanProductType'] == 1
                ? Num(data['Loan.ARMIndexValue']) + Num(data['Loan.ARMMargin'])
                : Num(data['Loan.interestRate']))) /
              100 /
              (data['Loan.InterimIntDaysPerYearOV'] == 2 ? 365 : 360)) *
              Math.pow(10, roundTo),
          ) / Math.pow(10, roundTo)
        break
      case 'Loan.EscrowDeposit':
        let sum = 0
        for (let i = 0; i < ppData.length - 1; i++) sum += Num(Number(removeComma(ppData[i][4])).toFixed(2))
        CALCULATED_VALUE = 0 - Number(data['Secondary.EscrowDepositIsNettedFromWire'] ? sum : 0)
        break
      case 'Loan.Netted':
        CALCULATED_VALUE = 0 - Math.round(Number(totalCCNetfromWires) * 100) / 100
        break
      case 'Loan._InterimInterest':
        if (data['Loan.DailyInterestOV'])
          CALCULATED_VALUE = Num(data['Loan.InterimInterestDays']) * Num(data['Loan.DailyInterestOV'])
        else {
          CALCULATED_VALUE = Num(data['Loan.InterimInterestDays']) * Num(calculateValue('Loan._DailyInterest'))
        }
        CALCULATED_VALUE = Math.round(CALCULATED_VALUE * 100) / 100
        break
      case 'Loan.CalculatedWireAmount':
        CALCULATED_VALUE =
          Math.round(
            (+data['Loan.BaseLoan'] +
              Num(data['Secondary.FundingWireAdjustmentAmount1']) +
              Num(data['Secondary.FundingWireAdjustmentAmount2']) +
              calculateValue('Loan.UFMIP') +
              calculateValue('Loan.Interim') +
              calculateValue('Loan.Netted') +
              calculateValue('Loan.EscrowDeposit')) *
              100,
          ) / 100
        break
      case 'Secondary.WarehouseAdvanceAmount':
        CALCULATED_VALUE =
          Math.round(
            Num(
              data['Secondary.WarehouseAdvanceCalcMethod'] == 1
                ? data['Loan.BaseLoan']
                : data['Secondary.FundingWireAmount'],
            ) * +data['Secondary.WarehouseAdvancePerc'],
          ) / 100
        break
      case 'Secondary._WarehouseHaircut':
        CALCULATED_VALUE = Num(data['Secondary.FundingWireAmount']) - calculateValue('Secondary.WarehouseAdvanceAmount')
        break
      default:
        return
    }
    if (key !== 'Loan._DailyInterest' && CALCULATED_VALUE)
      CALCULATED_VALUE = Number(Number(CALCULATED_VALUE).toFixed(2))
    return CALCULATED_VALUE
  }

  const onFieldNoteModalSubmit = async (valueKey: string, data: Record<string, any>) => {
    setEditField({ key: '', value: null })
    setIsLoading(true)
    const res = await posthud1Notes(valueKey, data)
    if (res.success) {
      const temp = cloneDeep(notes)
      temp[valueKey] = data
      setNotes(temp)
    }
    setIsLoading(false)
  }

  const handleContextMenu = (event: any, key: string, value: any) => {
    event.preventDefault()
    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect()
      setContextMenu({
        visible: true,
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
        key: key,
        value: value,
      })
    }
  }

  const closeContextMenu = () => {
    setContextMenu({
      visible: false,
      x: 0,
      y: 0,
      key: '',
      value: null,
    })
  }

  const renderInputs = (inputs: any) => {
    return (
      <>
        {(inputs || []).map((input: any) => {
          const { valueKey, valueType, inputType, overrideKey, canOverride } = input
          const calculatedValue = calculateValue(valueKey)
          const ovValue = overrideKey ? data[overrideKey] : undefined
          const hasOverride = ovValue !== undefined && ovValue !== null && ovValue !== ''
          input.value = hasOverride
            ? ovValue
            : !isEmpty(calculatedValue)
              ? calculatedValue
              : data[valueKey] !== undefined
                ? data[valueKey]
                : input.value || ''
          if (inputType == 'value') input.value = getPrice2decimal(input.value, false, true)
          else if (valueType === 'number') input.value = getPrice1or2decimal(input.value)
          if (typeof input.value === 'string' && input.value[0] === '-') input.value = `(${input.value.slice(1)})`
          // Party-sourced Title Company fields have no valueKey and are not HUD1-logged.
          input.history = !!valueKey
          input.bgColor = hasOverride ? 'red-50' : noteColors[notes[valueKey]?.flag]

          if (canOverride) {
            input.additionalElements = (
              <span
                className="mx-2 text-shade-blue cursor-pointer"
                onClick={() => {
                  setOVData({
                    title: input.title,
                    overrideKey,
                    calcValue: getPrice1or2decimal(calculatedValue),
                    ovValue: hasOverride ? ovValue : '',
                    input: {
                      inputType: 'text',
                      type: 'thousandSep',
                      title: input.title,
                      disabled: false,
                      additionalElements: null,
                      readOnly: false,
                      bgColor: '',
                    },
                  })
                }}
              >
                <PencilSquareIcon className="w-4 h-4" />
              </span>
            )
          }

          return (
            <div
              className={`input flex-1 col-span-${input.span ? input.span : 1}`}
              key={valueKey}
              onContextMenu={(e) => handleContextMenu(e, valueKey, data[overrideKey] || data[valueKey])}
            >
              <RenderInput
                input={input}
                Key={valueKey}
                key={valueKey}
                onChange={onChange}
                showHistory={() => showHistory(overrideKey ? [valueKey, overrideKey] : [valueKey])}
              />
            </div>
          )
        })}
      </>
    )
  }

  const onCopyClick = (key: string) => {
    const temp = cloneDeep(data)
    if (key == 'Secondary.FundingWireAmount') temp[key] = calculateValue('Loan.CalculatedWireAmount')
    setData(temp)
    setChanged(true)
  }

  const onDownload = async () => {
    setIsLoading(true)
    const pdfData = await downloadNetFundingSheet()
    downloadFile(`Net Funding WorkSheet ${loanNumber}(${formatDateYMD()}).pdf`, pdfData)
    setIsLoading(false)
  }

  useEffect(() => {
    if (!showEmailModal) return
    getEmailTemplatesGroup('closingFunding').then((data: EmailTemplate[]) => {
      setEmailTemplates(data || [])
      const newInput = cloneDeep(selectTypeInput)
      const options: Record<number, string> = {}
      ;(data || []).forEach((item) => (options[item.id] = item.title || item.subject))
      ;(newInput as any).options = options
      newInput.value = ''
      newInput.error = ''
      setSelectTypeInput(newInput)
      setContentInput((prev) => ({ ...prev, value: '', error: '' }))
    })
  }, [showEmailModal])

  const closeNetFundingEmailModal = () => {
    setNetFundingEmailRecipients({})
    setSelectTypeInput((prev) => ({ ...prev, value: '', error: '' }))
    setContentInput((prev) => ({ ...prev, value: '', error: '' }))
    setShowEmailModal(false)
  }

  const updateEmailContent = (templateId: string) => {
    const template = emailTemplates.find((item) => item.id == Number(templateId))
    if (!template) return false

    let { content } = template
    let result = true
    while (true) {
      const matched = content.match(/{{\w+}}/)
      if (!matched) break
      const foundBlock = matched[0]
      const foundKey = foundBlock.replace(/{|}/g, '')
      if (placeholderMap[foundKey] !== undefined) content = content.replace(foundBlock, placeholderMap[foundKey])
      else {
        toast(`${foundKey} is unknown variable.`, { type: 'warning' })
        result = false
        break
      }
    }

    setContentInput((prev) => ({ ...prev, value: content, error: '' }))
    return result
  }

  const onUpdateEmailType = (_: string, value: string) => {
    setSelectTypeInput((prev) => ({ ...prev, value, error: '' }))
    if (value) updateEmailContent(value)
    else setContentInput((prev) => ({ ...prev, value: '', error: '' }))
  }

  const onUpdateEmailContent = (_: string, value: string) => {
    setContentInput((prev) => ({ ...prev, value, error: '' }))
  }

  const onSendNetFundingSheetEmail = async () => {
    setEmailModalLoading(true)
    try {
      const template = emailTemplates.find((item) => item.id == Number(selectTypeInput.value))
      const subject = template?.subject
      const content = contentInput.value ? String(contentInput.value) : undefined
      const res = await sendNetFundingSheetEmail(netFundingEmailRecipients, subject, content)
      if (res.success) {
        closeNetFundingEmailModal()
      }
    } catch {
      toast('Failed to send email', { type: 'error' })
    } finally {
      setEmailModalLoading(false)
    }
  }

  return (
    <div className="HUD1Page3-container relative" ref={containerRef}>
      <LayoutLoading show={action !== '' || isLoading} />
      <Prompt
        when={changed}
        message={`You've made some changes on HUD1 Page3!\nAre you sure want to leave without Saving?`}
      />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center mb-3">
          Funding
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h2>
        <div className={`hover:underline cursor-pointer flex justify-end gap-2`}>
          <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={() => setShowEmailModal(true)}>
            <EnvelopeIcon className="w-5 h-5 text-shade-blue" />
          </span>
          <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
            <PrinterIcon className="w-5 h-5 text-shade-blue" />
          </span>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 grid-cols-1">
        <div className="col-span-2 my-4">{renderSectionHeader('Funding Key Dates')}</div>
        {renderInputs(inputs['funding_key_dates'])}
        <div className="col-span-2 my-4">
          <div className="grid md:grid-cols-12 sm:grid-cols-6 grid-cols-1 gap-2 my-4">
            {renderInputs(inputs['extendedFields'])}
          </div>
          <div className="grid md:grid-cols-12 sm:grid-cols-6 grid-cols-1 gap-2">
            <div className="md:col-span-4 sm:col-span-2 grid grid-cols-1 gap-2">
              {renderInputs(inputs['add_to_wire'])}
            </div>
            <div className="md:col-span-8 sm:col-span-6 grid md:grid-cols-2 grid-cols-1 gap-2">
              {renderInputs(inputs['included_in_wire'])}
            </div>
          </div>
          <div className="grid md:grid-cols-12 sm:grid-cols-6 grid-cols-1 gap-2 my-4">
            {renderInputs(inputs['totalToBroker'])}
          </div>
        </div>
        <div className="col-span-1 my-4">
          <div className="my-4">{renderSectionHeader('Funding Wire Calculation')}</div>
          <div className="grid md:grid-cols-2 grid-cols-1 gap-2">
            {renderInputs(inputs['funding_wire_calc'])}
            <div className={`input flex-1 col-span-1 flex items-center`}>
              <Button outline color="gray" onClick={() => onCopyClick('Secondary.FundingWireAmount')}>
                Fill
              </Button>
            </div>
          </div>
        </div>
        <div className="col-span-1 my-4">
          <div className="my-4">{renderSectionHeader('Dates')}</div>
          <div className="grid grid-cols-1 gap-2">{renderInputs(inputs['dates'])}</div>
        </div>
        <div className="col-span-2 my-4">{renderSectionHeader('Warehouse Info')}</div>
        {renderInputs(inputs['warehouse_info'])}
      </div>
      <div className="my-8">{renderSectionHeader('CONV. UFMIP')}</div>
      <div className="-mt-4">
        <PlainTable header={tableHeader} data={convData()} />
      </div>
      <div className="my-8">{renderSectionHeader('INTERIM INTEREST')}</div>
      <div className="-mt-4">
        <PlainTable header={tableHeader} data={interimInterestData()} />
      </div>
      <div className="my-8">{renderSectionHeader('FEES')}</div>
      <div className="-mt-4">
        <RenderInput
          input={{ title: 'Show Only Fees with Values', inputType: 'checkbox', value: showFeeWithValue }}
          Key={'showFeeWithValue'}
          onChange={() => setShowFeeWithValue(!showFeeWithValue)}
          showHistory={() => showHistory(['showFeeWithValue'])}
        />
        <div className=" max-h-196 overflow-auto">
          <PlainTable header={tableHeader} data={ccData} />
        </div>
      </div>
      <div className="my-8">{renderSectionHeader('ESCROW DEPOSIT')}</div>
      <div className="-mt-4">
        <RenderInput
          input={{ title: 'Show Only Escrowed Items', inputType: 'checkbox', value: showEscrowedItem }}
          Key={'showEscrowedItem'}
          onChange={() => setShowEscrowedItem(!showEscrowedItem)}
          showHistory={() => showHistory(['showEscrowedItem'])}
        />
        <PlainTable header={tableHeader} data={ppData} />
      </div>
      <div className="flex justify-end">
        <div className={`hover:underline cursor-pointer flex justify-end gap-2`}>
          <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={() => setShowEmailModal(true)}>
            <EnvelopeIcon className="w-5 h-5 text-shade-blue" />
          </span>
          <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
            <PrinterIcon className="w-5 h-5 text-shade-blue" />
          </span>
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <Button disabled={!changed} onClick={onSaveChanges} loading={action === 'saveChanges'}>
          Save Changes
        </Button>
      </div>
      <Disbursement />
      <SaveChanges show={changed} label="Save Changes" onSave={onSaveChanges} />
      {Object.keys(closingCostsDetailData).length > 0 && (
        <ClosingCostsDetailsModal
          closingCosts={closingCostsDetailData}
          loanNumber={loanNumber}
          loanAmount={data['HUD1.Line202Calc']}
          onClose={closeCostingDetailModal}
          onSubmit={closeCostingDetailModal}
          onDelete={onDeleteFee}
        />
      )}
      {Object.keys(prepaidItemsDetailData).length > 0 && (
        <PrepaidItemsDetailsModal
          prepaidItems={prepaidItemsDetailData}
          onClose={closeCostingDetailModal}
          onSubmit={closeCostingDetailModal}
        />
      )}

      {contextMenu.visible && (
        <CustomContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={setEditField}
          valueKey={contextMenu.key}
          value={contextMenu.value}
          showHistory={showHistory}
        />
      )}
      {editField.key && (
        <FieldNote
          valueKey={editField.key}
          currentValue={editField.value!}
          noteData={notes[editField.key]}
          onClose={() => setEditField({ key: '', value: null })}
          onOk={onFieldNoteModalSubmit}
        />
      )}

      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          key={ovData.overrideKey}
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          input={ovData.input}
          onClose={closeOVModal}
        />
      )}
      {showMIPFundingModal && <MIPFundingFeeModal data={data} onClose={closeMIPFundingModal} />}
      {showInterimInterestModal && <InterimInterestModal data={data} onClose={closeMIPFundingModal} />}
      {showEmailModal && (
        <Modal
          isOpen
          title="Send Net Funding Sheet via Email"
          titleOkay="Send Email"
          onOk={onSendNetFundingSheetEmail}
          onClose={closeNetFundingEmailModal}
          loading={emailModalLoading}
          disabled={!hasToEmails(netFundingEmailRecipients)}
          lastUpdatedAt={Date.now()}
        >
          <div className="w-full max-w-[650px]">
            <LoanPartiesEmailTo
              loanNumber={Number(loanNumber)}
              value={netFundingEmailRecipients}
              data={[]}
              onChange={setNetFundingEmailRecipients}
              loadParties
              modalId="netFundingSheetEmail"
            />
            <div className="w-full flex flex-col gap-2 mt-2">
              <RenderInput Key="section" input={{ inputType: 'section', title: 'Email' }} onChange={() => {}} />
              <RenderInput Key="type" input={selectTypeInput} onChange={onUpdateEmailType} />
              <RenderInput Key="content" input={contentInput} onChange={onUpdateEmailContent} />
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
